import { BookReviewsSection } from '@/components/book/reviews-section';
import { AiContentSection } from '@/components/public/ai-content-section';
import { RecommendedBooksSlider } from '@/components/recommended-books-slider';
import { SeoHead } from '@/components/seo-head';
import { SocialSharing, SocialSharingCompact } from '@/components/social-sharing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';
import { useTranslations } from '@/hooks/use-translations';
import PublicLayout from '@/layouts/public-layout';
import { Book } from '@/types/models';
import { Link } from '@inertiajs/react';
import { BookOpen, ExternalLink, Star, User } from 'lucide-react';
import { useState } from 'react';
interface Template {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    sections_count: number;
    has_content: boolean;
}

interface Props {
    book: Book;
    templates: Template[];
    locale: string;
    recommendedBooks?: Book[];
}

interface BookCoverProps {
    coverImage: string | null;
    title: string;
}

function BookCover({ coverImage, title }: BookCoverProps) {
    const [imageError, setImageError] = useState(false);
    const { t } = useTranslations();

    return (
        <div className="relative aspect-[2/3] overflow-hidden rounded-lg bg-muted shadow-xl">
            {coverImage && !imageError ? (
                <img src={coverImage} alt={title} className="h-full w-full object-contain" onError={() => setImageError(true)} />
            ) : (
                <div className="flex h-full items-center justify-center p-8">
                    <div className="text-center">
                        <BookOpen className="mx-auto mb-4 h-24 w-24 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">{t('No cover available')}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function BookDetail({ book, templates, locale, recommendedBooks }: Props) {
    const availableTemplates = templates.filter((t) => t.has_content);
    const unavailableTemplates = templates.filter((t) => !t.has_content);
    const { t } = useTranslations();
    const { genreUrl, authorUrl, bookUrl: getBookUrl } = useTranslatedRoutes();

    // Generate book URL for sharing (SSR-safe)
    const baseUrl = import.meta.env.VITE_APP_URL || 'http://localhost:8000';
    const bookUrl = baseUrl + getBookUrl(book.slug);

    // Generate comprehensive structured data for book following Schema.org/Book specification
    const bookJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Book',
        '@id': bookUrl,
        identifier: book.id, // Add book ID as identifier
        name: book.title,
        // Use translated title if available for current locale
        ...(book.translations?.find(t => t.language_code === locale)?.translated_title && {
            alternativeHeadline: book.translations.find(t => t.language_code === locale)?.translated_title,
        }),
        // Use translated description if available, fallback to original
        description: book.translations?.find(t => t.language_code === locale)?.translated_description || book.description,
        url: bookUrl,

        // Author information - can be string or Author object
        ...(book.author && {
            author: {
                '@type': 'Person',
                name: typeof book.author === 'string' ? book.author : book.author.name,
                ...(typeof book.author === 'object' && book.author.bio && { description: book.author.bio }),
                ...(typeof book.author === 'object' && (book.author.image || book.author.ai_image) && {
                    image: book.author.ai_image || book.author.image
                }),
                ...(book.author_slug && { url: baseUrl + authorUrl(book.author_slug) }),
            },
        }),

        // Cover images - prioritize AI-generated
        ...(( book.cover_image_ai || book.cover_image || book.cover_image_original) && {
            image: book.cover_image_ai || book.cover_image || book.cover_image_original,
            thumbnailUrl: book.cover_image_ai || book.cover_image || book.cover_image_original,
        }),

        // Publisher information from provider
        ...(book.provider && {
            publisher: {
                '@type': 'Organization',
                name: book.provider.name,
                ...(book.provider.domain && { url: `https://${book.provider.domain}` }),
            },
        }),

        // Genres
        ...(book.genres && book.genres.length > 0 && {
            genre: book.genres.map(g => g.name).join(', '), // Schema.org expects Text, not array
        }),

        // Language information
        inLanguage: locale || 'en',
        ...(book.translations && book.translations.length > 0 && {
            // Filter out current locale and only show other available languages
            availableLanguage: book.translations
                .filter(t => t.language_code !== locale && t.status === 'completed')
                .map(t => t.language_code),
        }),

        // Dates - all are required fields in our DB
        datePublished: book.created_at, // When book was added to our system
        dateModified: book.updated_at,
        ...(book.fetched_at && {
            dateCreated: book.fetched_at, // When originally fetched/scraped
        }),
        ...(book.cover_image_ai_generated_at && {
            // Track when AI cover was generated as additional metadata
            'schema:aiGeneratedDate': book.cover_image_ai_generated_at,
        }),

        // Source and reference URLs
        ...(book.source_url && {
            sameAs: book.source_url,
        }),

        // Aggregate rating - ensure we have valid numbers
        ...(book.rating_value && parseFloat(book.rating_value) > 0 && {
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: parseFloat(book.rating_value),
                ...(book.rating_count && parseInt(book.rating_count) > 0 && {
                    ratingCount: parseInt(book.rating_count)
                }),
                ...(book.review_count && parseInt(book.review_count) > 0 && {
                    reviewCount: parseInt(book.review_count)
                }),
                bestRating: 5,
                worstRating: 1,
            },
        }),

        // Individual reviews - only include approved reviews
        ...(book.reviews && book.reviews.length > 0 && {
            review: book.reviews
                .filter(review => review.is_approved !== false) // Include if approved or not specified
                .map(review => ({
                    '@type': 'Review',
                    author: {
                        '@type': 'Person',
                        name: review.reviewer_name,
                    },
                    reviewBody: review.review_text,
                    reviewRating: {
                        '@type': 'Rating',
                        ratingValue: review.rating,
                        bestRating: 5,
                        worstRating: 1,
                    },
                    datePublished: review.review_date,
                    ...(review.language_code && { inLanguage: review.language_code }),
                    ...(review.helpful_count && review.total_votes && {
                        interactionStatistic: {
                            '@type': 'InteractionCounter',
                            interactionType: 'https://schema.org/LikeAction',
                            userInteractionCount: review.helpful_count,
                        },
                    }),
                })),
        }),

        // Offers/Availability
        ...(book.source_url && {
            offers: {
                '@type': 'Offer',
                availability: book.published ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                price: '0',
                priceCurrency: 'USD',
                url: book.source_url,
            },
        }),

        // Work translation information
        ...(book.is_translated && {
            translationOfWork: {
                '@type': 'Book',
                name: book.title,
                inLanguage: 'en',
            },
        }),

        // Additional metadata for search engines
        ...(book.published !== undefined && {
            'schema:isAccessibleForFree': true,
            'schema:isAvailable': book.published,
        }),
    };

    return (
        <PublicLayout>
            <SeoHead
                title={book.title}
                description={book.description?.substring(0, 160)}
                ogImage={book.cover_image_ai ?? book.cover_image ?? undefined}
                ogType="book"
                author={typeof book.author === 'string' ? book.author : book.author?.name}
                jsonLd={bookJsonLd}
            />

            {/* Minimal Book Header */}
            <section className="py-6 md:py-10">
                <div className="container mx-auto px-4">
                    <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:gap-12">
                        {/* Book Cover - Left column */}
                        <div className="mx-auto w-full max-w-[200px] flex-shrink-0 sm:max-w-[250px] md:mx-0 md:max-w-[300px]">
                            <BookCover coverImage={book.cover_image_ai || book.cover_image || null} title={book.title} />

                            {/* Social Sharing under book cover */}
                            <div className="mt-4 flex justify-center">
                                <SocialSharing
                                    url={bookUrl}
                                    title={`${book.title} by ${book.author}`}
                                    description={`Check out ${book.title} by ${book.author}${book.genres && book.genres.length > 0 ? ` - ${book.genres.map((g) => g.name).join(', ')}` : ''}`}
                                />
                            </div>
                        </div>

                        {/* Book Info - Right column */}
                        <div className="flex-1 space-y-4 md:space-y-6">
                            {/* Title and Author */}
                            <div>
                                <h1 className="mb-2 text-xl font-medium sm:text-2xl md:text-3xl">{book.title}</h1>
                                <p className="text-base text-muted-foreground sm:text-lg">
                                    {t('by')}:{' '}
                                    {book.author && book.author_slug ? (
                                        <Link href={authorUrl(book.author_slug)} className="transition-colors hover:text-foreground">
                                            {typeof book.author === 'string' ? book.author : book.author.name}
                                        </Link>
                                    ) : book.author ? (
                                        <span>{typeof book.author === 'string' ? book.author : book.author.name}</span>
                                    ) : (
                                        <span className="text-muted-foreground">{t('Unknown')}</span>
                                    )}
                                </p>
                            </div>

                            {/* Rating - Single line if available */}
                            {book.rating_value && (
                                <div className="flex items-center gap-2">
                                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                    <span className="font-medium">{book.rating_value}</span>
                                    {book.rating_count && (
                                        <span className="text-sm text-muted-foreground">
                                            ({t(':count ratings', { count: book.rating_count.toLocaleString() })})
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Book Description */}
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                                <AiContentSection name="book-description" />
                            </div>

                            {/* Genres - Minimal badges */}
                            {book.genres && book.genres.length > 0 && (
                                <div className="flex flex-wrap gap-2 md:gap-2.5">
                                    {book.genres.map((genre) => (
                                        <Link key={genre.id} href={genreUrl(genre.slug)}>
                                            <Badge
                                                variant="outline"
                                                className="px-3 py-1.5 text-sm font-normal transition-all hover:border-primary hover:bg-primary hover:text-primary-foreground sm:px-4 sm:py-2 sm:text-base"
                                            >
                                                {genre.name}
                                            </Badge>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {/* Meta info - Very subtle */}
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground sm:text-base md:gap-5">
                                {book.created_at && <span>{t('Added :date', { date: new Date(book.created_at).toLocaleDateString('en-GB') })}</span>}
                                {book.source_url && (
                                    <a
                                        href={book.source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="-mx-3 -my-1.5 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-all hover:bg-muted hover:text-foreground"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        {book.source_url.includes('goodreads')
                                            ? t('Goodreads')
                                            : book.source_url.includes('amazon')
                                              ? t('Amazon')
                                              : book.source_url.includes('bookdepository')
                                                ? t('Book Depository')
                                                : book.source_url.includes('barnesandnoble')
                                                  ? t('Barnes & Noble')
                                                  : t('View source')}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Iconic Quote - Minimal and Elegant */}
            <section className="relative overflow-hidden bg-muted/10 py-8 md:py-12">
                {/* Subtle background pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 left-0 h-40 w-40 -translate-x-1/2 -translate-y-1/2 font-serif text-9xl text-foreground">"</div>
                    <div className="absolute right-0 bottom-0 h-40 w-40 translate-x-1/2 translate-y-1/2 rotate-180 font-serif text-9xl text-foreground">
                        "
                    </div>
                </div>

                <div className="relative container mx-auto px-6 md:px-4">
                    <div className="mx-auto max-w-3xl text-center">
                        <div className="prose prose-base sm:prose-lg dark:prose-invert mx-auto">
                            <AiContentSection name="iconic-quote" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Literary Analysis - Clean and Minimal */}
            <section className="py-6 md:py-10">
                <div className="container mx-auto px-4">
                    <div className="mx-auto max-w-4xl">
                        <div className="mb-6 flex items-center gap-3 md:mb-8 md:gap-4">
                            <BookOpen className="h-4 w-4 text-muted-foreground md:h-5 md:w-5" />
                            <h2 className="text-xl font-medium md:text-2xl">{t("Let's Break This Down")}</h2>
                            <div className="h-px flex-1 bg-border" />
                        </div>

                        <div className="space-y-4 md:space-y-6">
                            {/* Writing Style */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t("The Author's Voice")}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="writing-style-breakdown" />
                                </div>
                            </div>

                            {/* Key Takeaways */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t('Key Moments')}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="highlights" />
                                </div>
                                <div className="mt-8">
                                    <AiContentSection name="summary-and-analysis" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Book Content */}
            <div className="bg-gradient-to-b from-background to-muted/20">
                {/* AI-Generated Visual Hero */}
                <section className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
                    <div className="relative container mx-auto px-4 py-6 md:py-10">
                        <div className="mx-auto max-w-4xl">
                            <div className="mx-auto max-w-3xl">
                                <AiContentSection name="ai-generated-image" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Quick Hook & Synopsis */}
                <section className="relative -mt-12 pb-6 md:-mt-16 md:pb-10">
                    <div className="container mx-auto px-4">
                        <div className="mx-auto max-w-4xl">
                            <div className="mx-auto w-4/5 max-w-2xl">
                                {/* Hook Tagline - Floating Card */}
                                <div className="rounded-xl bg-card/95 p-6 text-center shadow-2xl backdrop-blur md:rounded-2xl md:p-8">
                                    <AiContentSection name="hook-tagline" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Reader Insights - Minimal */}
            <section className="py-6 md:py-10">
                <div className="container mx-auto px-4">
                    <div className="mx-auto max-w-4xl">
                        <div className="mb-6 flex items-center gap-3 md:mb-8 md:gap-4">
                            <User className="h-4 w-4 text-muted-foreground md:h-5 md:w-5" />
                            <h2 className="text-xl font-medium md:text-2xl">{t('What Readers Are Saying')}</h2>
                            <div className="h-px flex-1 bg-border" />
                        </div>

                        <div className="space-y-4 md:space-y-6">
                            {/* Target Audience */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t('Right for You If')}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="target-audience" />
                                </div>
                            </div>

                            {/* Synopsis */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t("What You're Getting Into")}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="spoiler-free-synopsis" />
                                </div>
                            </div>

                            {/* Characters */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t("Characters You'll Meet")}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="main-characters" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Similar Books Section */}
            <section className="py-6 md:py-10">
                <div className="container mx-auto px-4">
                    <div className="mx-auto max-w-4xl">
                        <div className="mb-6 flex items-center gap-3 md:mb-8 md:gap-4">
                            <BookOpen className="h-4 w-4 text-muted-foreground md:h-5 md:w-5" />
                            <h2 className="text-xl font-medium md:text-2xl">{t('More Like This')}</h2>
                            <div className="h-px flex-1 bg-border" />
                        </div>
                        <div className="p-4 sm:p-5 md:p-6">
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                                <AiContentSection name="comparisons" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Expert Review Section - Emphasized */}
            <section className="bg-muted/30 py-6 md:py-10">
                <div className="container mx-auto px-4">
                    <div className="mx-auto max-w-4xl">
                        <div className="mb-6 flex items-center gap-3 md:mb-8 md:gap-4">
                            <Star className="h-4 w-4 text-muted-foreground md:h-5 md:w-5" />
                            <h2 className="text-xl font-medium md:text-2xl">{t("Critic's Corner")}</h2>
                            <div className="h-px flex-1 bg-border" />
                        </div>
                        <div className="bg-card p-5 md:p-8">
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                                <AiContentSection name="expert-review" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reviews Section */}
            <BookReviewsSection reviews={book.reviews} bookTitle={book.title} bookSlug={book.slug} />

            {/* Context & Discussion */}
            <section className="py-6 md:py-10">
                <div className="container mx-auto px-4">
                    <div className="mx-auto max-w-4xl">
                        <div className="mb-6 flex items-center gap-3 md:mb-8 md:gap-4">
                            <svg className="h-4 w-4 text-muted-foreground md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                />
                            </svg>
                            <h2 className="text-xl font-medium md:text-2xl">{t('Local Take')}</h2>
                            <div className="h-px flex-1 bg-border" />
                        </div>

                        <div className="space-y-4 md:space-y-6">
                            {/* Local Perspective */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t('Why It Matters')}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="local-perspective" />
                                </div>
                            </div>

                            {/* Points of Discussion */}
                            <div className="p-4 sm:p-5 md:p-6">
                                <h3 className="mb-4 text-lg font-medium">{t('Food for Thought')}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <AiContentSection name="controversies" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA - Personalized recommendations primary, share secondary */}
            <section className="bg-gradient-to-t from-muted/30 to-transparent py-8 md:py-12">
                <div className="container mx-auto px-4">
                    <div className="mx-auto max-w-2xl text-center">
                        {/* Primary CTA - Personalized Recommendations */}
                        <div className="mb-8">
                            <h2 className="mb-4 text-2xl font-semibold md:text-3xl">{t('Want personalized recommendations?')}</h2>
                            <p className="mb-8 text-base text-muted-foreground md:text-lg">{t('Get tailored book picks in minutes')}</p>
                            <div className="flex justify-center">
                                <Button asChild size="lg" className="gap-2 px-8 py-3 text-lg">
                                    <a href="http://link.brajti.com/brajti-start?utm_source=blog_brajti" target="_blank" rel="noopener noreferrer">
                                        {t('Start quiz')}
                                    </a>
                                </Button>
                            </div>
                        </div>

                        {/* Simple Divider */}
                        <div className="my-8">
                            <div className="w-full border-t border-border"></div>
                        </div>

                        {/* Secondary CTA - Share */}
                        <div className="text-center">
                            <p className="mb-3 text-sm text-muted-foreground">
                                {t('Like what you see?')} {t('Share it with other readers')}
                            </p>
                            <div className="flex justify-center">
                                <SocialSharingCompact
                                    url={bookUrl}
                                    title={`${book.title} by ${book.author}`}
                                    description={`Check out ${book.title} by ${book.author}${book.genres && book.genres.length > 0 ? ` - ${book.genres.map((g) => g.name).join(', ')}` : ''}`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Recommended Books Section */}
            {recommendedBooks && <RecommendedBooksSlider books={recommendedBooks} />}
        </PublicLayout>
    );
}
